import IPhysicalAnalysisFailure from 'types/model/IPhysicalAnalysisFailure';

// PLA-1077 v2: texto del tooltip que explica por que un campo del cierre del asistente
// no cumple su parametro. Funcion pura: recibe `t` para poder probarla sin el I18n global.
type Translate = (key: string, options?: { [key: string]: string }) => string;

export interface FailureExplanation {
  title: string;
  lines: string[];
}

const SCOPE = 'physical_analysis.failure_info';

const formatNumber = (value: number): string => value.toFixed(2);

const withUnit = (value: number, unit: IPhysicalAnalysisFailure['unit']): string => {
  if (unit === 'percentage') {
    return `${formatNumber(value)} %`;
  }

  if (unit === 'gram') {
    return `${formatNumber(value)} gr`;
  }

  return formatNumber(value);
};

const measuredText = (detail: IPhysicalAnalysisFailure, t: Translate): string | null => {
  if (detail.measured === null) {
    return null;
  }

  const value = withUnit(detail.measured, detail.unit);

  if (detail.grams === null || detail.green === null) {
    return t(`${SCOPE}.measured`, { value });
  }

  return t(`${SCOPE}.measured`, {
    value: t(`${SCOPE}.of_green`, {
      value,
      grams: formatNumber(detail.grams),
      green: formatNumber(detail.green),
    }),
  });
};

// Los limites son estrictos (PurchasePhysicalAnalysisParameter#violation): igualar el
// limite tambien es incumplirlo, por eso "menor que" / "mayor que".
const allowedText = (detail: IPhysicalAnalysisFailure, t: Translate): string | null => {
  const { min, max, unit } = detail;

  if (min !== null && max !== null) {
    return t(`${SCOPE}.allowed_range`, {
      min: withUnit(min, unit),
      max: withUnit(max, unit),
    });
  }

  if (max !== null) {
    return t(`${SCOPE}.allowed_max`, { max: withUnit(max, unit) });
  }

  if (min !== null) {
    return t(`${SCOPE}.allowed_min`, { min: withUnit(min, unit) });
  }

  return null;
};

export const failureExplanation = (
  detail: IPhysicalAnalysisFailure,
  t: Translate = (key, options) => I18n.translate(key, options)
): FailureExplanation => {
  const process =
    detail.processType || t('purchase_physical_analysis_parameters.any_process');

  const lines = [
    measuredText(detail, t),
    allowedText(detail, t),
    t(`${SCOPE}.process`, { process }),
  ].filter((line): line is string => line !== null);

  return { title: t(`${SCOPE}.title.${detail.violation}`), lines };
};
